import React, { useMemo } from 'react';
import type { BandwidthActivityAllocationRow } from './bandwidthDashboardTypes';
import { BandwidthAllocationPanel, BANDWIDTH_ACTIVITY_ALLOCATION_COLUMNS, useAllocationSort, type AllocationTableRow } from './BandwidthAllocationTable';
import { computeActivityAllocationFooter, sortActivityAllocationRows } from './bandwidthAllocation';
import { exportBandwidthActivityAllocationExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthActivityAllocationPdf } from './bandwidthDashboardPdfExport';

export interface BandwidthActivityAllocationTableProps {
  rows: BandwidthActivityAllocationRow[];
  loading?: boolean;
  subtitle?: string;
  exportDateFrom: string;
  exportDateTo: string;
  selectedActivityType: string | null;
  onActivityClick: (activityType: string) => void;
}

const BandwidthActivityAllocationTable: React.FC<BandwidthActivityAllocationTableProps> = ({
  rows,
  loading = false,
  subtitle,
  exportDateFrom,
  exportDateTo,
  selectedActivityType,
  onActivityClick,
}) => {
  const { sortKey, sortDir, toggleSort } = useAllocationSort('totalHours');

  const sortedRows = useMemo(
    () => sortActivityAllocationRows(rows, sortKey, sortDir),
    [rows, sortKey, sortDir]
  );

  const flatRows = useMemo(
    (): AllocationTableRow[] =>
      sortedRows.map((row) => ({
        id: row.activityType,
        label: row.activityTypeLabel,
        rowKind: 'activity',
        activityType: row.activityType,
        selected: selectedActivityType === row.activityType,
        metrics: row,
      })),
    [sortedRows, selectedActivityType]
  );

  const footer = useMemo(() => computeActivityAllocationFooter(sortedRows), [sortedRows]);

  return (
    <BandwidthAllocationPanel
      title="By Activity View"
      subtitle={subtitle}
      labelColumnTitle="Activity"
      rows={flatRows}
      footer={footer}
      columns={BANDWIDTH_ACTIVITY_ALLOCATION_COLUMNS}
      loading={loading}
      horizontalScroll
      sortKey={sortKey}
      sortDir={sortDir}
      onToggleSort={toggleSort}
      onActivityClick={onActivityClick}
      exportAriaLabel="Export activity allocation"
      buildExcel={() => exportBandwidthActivityAllocationExcel(sortedRows, exportDateFrom, exportDateTo)}
      buildPdf={() => exportBandwidthActivityAllocationPdf(sortedRows, exportDateFrom, exportDateTo)}
    />
  );
};

export default BandwidthActivityAllocationTable;
